import { type ReactElement, useEffect, useRef, useState } from 'react';

import { useLocation } from 'react-router';

import { useAppDispatch, useAppSelector } from 'dux/app/hooks';

import { theme } from '@prose-ui';
import { keyframes, legacyTheme, styled } from '@prose-ui/legacy';
import { animated, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import MarkdownIt from 'markdown-it';

import { ListOfImages } from 'Apps/Consultation/Components/ListOfImages';
import { SliderFragranceTestimonyCards } from 'Apps/Consultation/Components/SliderFragranceTestimonyCards';
import {
  type SlideContent,
  SliderImageAndContent,
} from 'Apps/Consultation/Components/SliderImageAndContent';
import { type Slider, SliderOfSlider } from 'Apps/Consultation/Components/SliderOfSlider';
import * as consultationTipsComponents from 'Apps/Consultation/constants/consultationTipsComponents';
import type { ProductsCategories } from 'Apps/PostPurchase/Containers/PostPurchaseSuccessV2';

import { headerHeight } from 'Components/ThemeProvider';
import { Typography } from 'Components/Typography';

import { productsCategories } from 'constants/products';

import type { ConsultationCategoryProp } from 'PropTypes/consultationProps';

import { handleKeyPress } from 'utils/a11y';
import useHeight from 'utils/useHeight';
import usePreventBodyScroll from 'utils/usePreventBodyScroll';

import { trackHeapEvent } from 'dux/tracking/actions';
import * as consultationSelectors from 'dux/consultation/selectors';

import type { ConsultationTipImage } from './ImageWithLegend';

const md = new MarkdownIt({ html: true });

const Background = styled.div<{ bottomoffset: number }>`
  position: fixed;
  z-index: 89;
  top: 0;
  left: 0;
  bottom: ${({ bottomoffset }) => bottomoffset}px;
`;

const Root = styled.div<{ bottomoffset: number }>`
  position: fixed;
  bottom: ${({ bottomoffset }) => bottomoffset}px;
  left: ${legacyTheme.spacing.s16};
  right: ${legacyTheme.spacing.s16};
  z-index: 90;

  background-color: ${theme.colors.tertiary[200]};
  border-top-left-radius: ${legacyTheme.spacing.s24};
  border-top-right-radius: ${legacyTheme.spacing.s24};

  ${legacyTheme.breakpoints.up('md')} {
    left: ${legacyTheme.spacing.s72};
    width: calc(50% - ${legacyTheme.spacing.s72});
  }
`;

const TipHeader = styled.div`
  padding: ${legacyTheme.spacing.s16} 0;
  cursor: pointer;
`;

const TipBody = styled(animated.div)`
  overflow: hidden;

  display: flex;
  flex-direction: column;
`;

const RichContentContainer = styled.div`
  flex: 1;
  width: 100%;
  max-width: 400px;
  margin: 0 auto 20px;
`;

const TipBodyContent = styled.div`
  overflow-y: auto;

  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0 ${legacyTheme.spacing.s12};
`;

const Content = styled(Typography)`
  margin-bottom: ${legacyTheme.spacing.s16};

  & > p {
    margin: 0;
  }
`;

const TipFooter = styled.div`
  padding: ${legacyTheme.spacing.s16} 0;
  cursor: pointer;
  text-align: center;
`;

const tipBounceKeyframes = keyframes`
  0% { height: 0;}
  25% { height: 4px; }
  50% { height: 0;}
  70% { height: 2px;}
  90% { height: 0; }
  100% { height: 0; }
`;

const BouncingTip = styled.div`
  width: 100%;
  height: 0;
  /* @Eric: this variable act as a css toggle. initial means the fallback value wihin the var() will be applied. */
  --is-animated: none;

  ${tipBounceKeyframes};
  animation-name: var(--is-animated, ${tipBounceKeyframes.name});
  animation-duration: 1200ms;
  animation-iteration-count: infinite;
  animation-timing-function: ease;
`;

const heapDomain = 'Consultation - Tip';

const consultationTipsComponentsList = {
  ListOfImages,
  SliderFragranceTestimonyCards,
  SliderImageAndContent,
  SliderOfSlider,
};

type RichContentComponentMap = {
  [key in keyof typeof consultationTipsComponentsList]: React.ComponentType<
    React.ComponentProps<(typeof consultationTipsComponentsList)[key]>
  >;
};

const richContentComponentMap: RichContentComponentMap = {
  [consultationTipsComponents.ListOfImages]: ListOfImages,
  [consultationTipsComponents.SliderFragranceTestimonyCards]: SliderFragranceTestimonyCards,
  [consultationTipsComponents.SliderImageAndContent]: SliderImageAndContent,
  [consultationTipsComponents.SliderOfSlider]: SliderOfSlider,
};

type FragranceTestimony = {
  by: string;
  fragranceName: string;
  intensity: number;
  notes: string;
  testimony: string;
  title: string;
};

type RichContent = {
  Component: keyof typeof consultationTipsComponents;
  contentComesFirst?: boolean;
  fragranceTestimonies?: FragranceTestimony[];
  images?: ConsultationTipImage[];
  slidersForStylingGel?: Slider[];
  sliders?: Slider[];
  slides?: ({ hairTexture }: { hairTexture: string }) => SlideContent[];
  slidesContent?: SlideContent[];
};

type ConsultationTipProps = {
  bottomoffset?: number;
  consultationCategory?: ConsultationCategoryProp;
  tip?: {
    content?:
      | {
          [key in ProductsCategories]?: string;
        }
      | string
      | ReactElement;
    couldBounce?: boolean;
    richContent?: RichContent;
    title: string;
    videoId?: string;
  };
};

export const ConsultationTip = ({
  bottomoffset = 0,
  consultationCategory,
  tip,
}: ConsultationTipProps) => {
  const dispatch = useAppDispatch();
  // @ts-expect-error - The consultation DUX has no TS types yet
  const haircareFragrances = useAppSelector(consultationSelectors.getFragrancesData);
  // @ts-expect-error - The consultation DUX has no TS types yet
  const skincareFragrances = useAppSelector(consultationSelectors.getSkincareFragrancesData);
  const haircareTexture = useAppSelector(consultationSelectors.getHairTexture);
  const { pathname } = useLocation();
  const contentRef = useRef();
  const footerRef = useRef<HTMLDivElement>(null);
  const contentHeight = useHeight(contentRef);
  const footerHeight = useHeight(footerRef);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [isOpened, setIsOpened] = useState(false);
  // @Damien: when tip is opened prevent scroll from happening
  usePreventBodyScroll(isOpened);

  useEffect(() => {
    setIsOpened(false);
    setHasBeenOpened(false);
  }, [pathname]);

  const availableHeight =
    typeof window !== 'undefined' ? window.innerHeight - headerHeight - 66 - bottomoffset : 0;

  const tipBodyStyles = useSpring({
    height: isOpened ? availableHeight : 0,
  });

  const toggleIsOpened = () => {
    const futureStateIsOpened = !isOpened;
    setIsOpened(futureStateIsOpened);

    dispatch(trackHeapEvent(`${heapDomain} ${futureStateIsOpened ? 'Open' : 'Close'}`));

    if (futureStateIsOpened && !hasBeenOpened) {
      setHasBeenOpened(true);
    }
  };

  const bind = useDrag(
    ({ swipe: [, swipeY] }) => {
      if (isOpened && swipeY === 1) {
        toggleIsOpened();
      }
    },
    {
      swipe: {
        // @Damien: fine tuned these threshold values while playing with the gesture
        distance: 7,
        velocity: 0.5,
        duration: 500,
      },
    },
  );

  const { content, couldBounce, richContent, title } = tip ?? {};
  const fragrancesMap = {
    [productsCategories.AGNOSTIC]: [],
    [productsCategories.HAIRCARE]: haircareFragrances,
    [productsCategories.SKINCARE]: skincareFragrances,
  };
  const fragrances =
    typeof consultationCategory !== 'undefined' && fragrancesMap[consultationCategory];
  const RichContentComponent = richContent && richContentComponentMap[richContent?.Component];
  const richContentHeight = availableHeight - contentHeight - footerHeight;
  const shouldBounce = couldBounce && !hasBeenOpened;
  // @ts-expect-error - (@pierre)  TODO: fix this TS error
  const tipContent = content?.[consultationCategory] ?? content;

  // @Damien: prerender text here once, it could be placed at 2 different location
  const TextContent = content && (
    <Content
      ref={contentRef}
      align="center"
      color="noir"
      dangerouslySetInnerHTML={{ __html: md.render(tipContent) }}
      variant="p2"
    />
  );

  return (
    <>
      {isOpened && (
        <Background
          bottomoffset={bottomoffset}
          data-click="tip-close"
          data-from="consultation"
          onClick={toggleIsOpened}
        />
      )}

      <Root {...bind()} bottomoffset={bottomoffset} data-testid="consultation-drawer-tip">
        <>
          <TipHeader
            aria-controls="consultation-drawer-tip-body"
            aria-expanded={isOpened}
            aria-label={isOpened ? 'Close tip' : 'Open tip to help you answer'}
            data-click={isOpened ? 'tip-close' : 'tip-open'}
            data-from="consultation"
            data-testid="consultation-drawer-tip-header"
            id="consultation-drawer-tip-header"
            onClick={toggleIsOpened}
            onKeyPress={handleKeyPress(toggleIsOpened)}
            role="button"
            tabIndex={0}
          >
            <Typography align="center" variant="mono2">
              {title}
            </Typography>
          </TipHeader>

          <TipBody
            aria-hidden={!isOpened}
            aria-labelledby="consultation-drawer-tip-header"
            data-testid="consultation-drawer-tip-body"
            id="consultation-drawer-tip-body"
            style={tipBodyStyles}
          >
            <TipBodyContent>
              {richContent?.contentComesFirst && TextContent}
              {RichContentComponent && (
                <RichContentContainer>
                  <RichContentComponent
                    availableHeight={richContentHeight}
                    fragranceTestimonies={
                      // @Damien: testimonies are synschronized with the fragrances from API
                      (fragrances?.length &&
                        richContent?.fragranceTestimonies?.filter((testimony) =>
                          fragrances.find(
                            // @ts-expect-error - The getFragrancesData and getSkincareFragrancesData selectors are not yet typed
                            (fragrance) => fragrance.name === testimony?.fragranceName,
                          ),
                        )) ||
                      []
                    }
                    heapDomain={heapDomain}
                    images={richContent?.images ?? []}
                    isOnScreen={isOpened}
                    sliders={richContent?.sliders ?? []}
                    slidesContent={
                      richContent?.slides
                        ? richContent.slides({ hairTexture: haircareTexture })
                        : []
                    }
                  />
                </RichContentContainer>
              )}
              {!richContent?.contentComesFirst && TextContent}
            </TipBodyContent>

            <TipFooter
              ref={footerRef}
              aria-controls="consultation-drawer-tip-body"
              aria-label="Close tip"
              data-click="tip-close"
              data-from="consultation"
              data-testid="consultation-drawer-tip-footer"
              id="consultation-drawer-tip-footer"
              onClick={toggleIsOpened}
              onKeyPress={handleKeyPress(toggleIsOpened)}
              role="button"
              tabIndex={isOpened ? 0 : -1}
            >
              <Typography inline markupName="span" variant="mono3">
                CLOSE x
              </Typography>
            </TipFooter>
          </TipBody>
        </>

        <BouncingTip
          data-testid="consultation-drawer-tip-bouncer"
          style={{ '--is-animated': shouldBounce && !isOpened ? 'initial' : '' }}
        />
      </Root>
    </>
  );
};
